import { useCallback, useEffect, useMemo, useState } from 'react';
import { Bookmark, CalendarRange, CheckCircle2, Download, Loader2, ShieldAlert, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { DateRange } from 'react-day-picker';

import {
  apiClient,
  type AuditDbFilters,
  type AuditFilters,
  type AuditSavedSearch,
  type AuditVerifyStatus
} from '@/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import AuditDbEventTable from './AuditDbEventTable';
import AuditEventTable from './AuditEventTable';
import AuditOverview from './AuditOverview';
import AuditUsersTable from './AuditUsersTable';
import UserIdentifierCombobox from './UserIdentifierCombobox';

const VERIFY_REFRESH_MS = 5 * 60 * 1000;
const EXPORT_LIMIT = 100000;
const EXPORT_FORMATS = ['csv', 'jsonl', 'json', 'xlsx', 'pdf'] as const;

const EVENT_TYPE_OPTIONS = [
  { group: 'Auth', values: ['auth.login', 'auth.login_failed', 'auth.logout', 'auth.session_end'] },
  { group: 'Command', values: ['cmd.proposed', 'cmd.approved', 'cmd.denied', 'cmd.executed'] },
  { group: 'Data', values: ['data.read', 'data.export'] },
  {
    group: 'Admin',
    values: [
      'admin.role_changed',
      'admin.user_created',
      'admin.user_disabled',
      'admin.user_deleted',
      'admin.user_data_purged',
      'admin.temp_changed',
      'admin.signup_policy_changed',
      'admin.password_reset_triggered'
    ]
  },
  { group: 'Signup', values: ['signup.requested', 'signup.approved', 'signup.denied'] },
  { group: 'Password Reset', values: ['password_reset.requested', 'password_reset.completed'] },
  { group: 'Legal', values: ['legal.accept'] },
  { group: 'System', values: ['system.startup', 'system.shutdown'] }
];

// audit_event only ever carries these sources. Postgres changes live in the DB Events
// tab (audit_db_event); kali_controller events are logged controller-side, not yet ingested (#895).
const SOURCE_OPTIONS = ['chainlit', 'legal', 'system'];
const SEVERITY_OPTIONS = ['critical', 'warning', 'notice', 'info'];
const RANGE_PRESETS = [
  { label: 'Last 24h', days: 1 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 }
];
const DB_TABLE_OPTIONS = ['users', 'threads', 'steps', 'operations', 'user_legal_acceptance'];
const DB_OP_OPTIONS = ['INSERT', 'UPDATE', 'DELETE'];

function VerifyBadge({ status, refreshing }: { status: AuditVerifyStatus | null; refreshing: boolean }) {
  if (refreshing && !status) {
    return (
      <Badge variant="outline" className="gap-1">
        <Loader2 className="h-3 w-3 animate-spin" /> Verifying…
      </Badge>
    );
  }
  if (!status) return null;
  if (status.ok) {
    return (
      <Badge variant="outline" className="gap-1 text-green-600 border-green-500/40">
        <CheckCircle2 className="h-3 w-3" /> Chain OK · {status.rows_checked} rows
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1" title={status.error ?? undefined}>
      <ShieldAlert className="h-3 w-3" /> Chain integrity error
    </Badge>
  );
}

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function endOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(23, 59, 59, 999);
  return copy;
}

function rangeLabel(since?: string, until?: string): string {
  if (!since && !until) return 'All time';
  if (since && !until) {
    const days = Math.round((Date.now() - new Date(since).getTime()) / 86_400_000);
    const preset = RANGE_PRESETS.find((option) => option.days === days);
    if (preset) return preset.label;
  }
  const show = (value?: string) => (value ? new Date(value).toLocaleDateString() : '…');
  return `${show(since)} – ${show(until)}`;
}

/** Preset + custom date-range picker writing ISO ``since`` / ``until`` filters. */
function DateRangeFilter({
  since,
  until,
  onChange
}: {
  since?: string;
  until?: string;
  onChange: (since?: string, until?: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected: DateRange | undefined = since
    ? { from: new Date(since), to: until ? new Date(until) : undefined }
    : undefined;

  const applyPreset = (days: number) => {
    onChange(new Date(Date.now() - days * 86_400_000).toISOString(), undefined);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="justify-start font-normal">
          <CalendarRange className="h-4 w-4 mr-2" /> {rangeLabel(since, until)}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2">
        <div className="flex flex-wrap gap-1 pb-2">
          {RANGE_PRESETS.map((option) => (
            <Button key={option.days} variant="ghost" size="sm" onClick={() => applyPreset(option.days)}>
              {option.label}
            </Button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onChange(undefined, undefined);
              setOpen(false);
            }}
          >
            All time
          </Button>
        </div>
        <Calendar
          mode="range"
          numberOfMonths={2}
          selected={selected}
          onSelect={(range) =>
            onChange(
              range?.from ? startOfDay(range.from).toISOString() : undefined,
              range?.to ? endOfDay(range.to).toISOString() : undefined
            )
          }
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Top-level "Audit Log" admin section.
 *
 * Owns the filter bar, tab switcher (events / db events), verify badge,
 * export button, and tail toggle. Delegates row rendering to the per-table
 * components so the per-user dialog reuses the same workhorse.
 */
type Props = {
  initialUserIdentifier?: string;
};

export default function AuditLogSection({ initialUserIdentifier }: Props = {}) {
  const [filters, setFilters] = useState<AuditFilters>(() => ({
    limit: 100,
    ...(initialUserIdentifier ? { user_identifier: initialUserIdentifier } : {})
  }));
  useEffect(() => {
    if (initialUserIdentifier === undefined) return;
    setFilters((prev) => ({ ...prev, user_identifier: initialUserIdentifier || undefined }));
  }, [initialUserIdentifier]);
  const [dbFilters, setDbFilters] = useState<AuditDbFilters>({ limit: 100 });
  const [tailing, setTailing] = useState(false);
  const [verifyStatus, setVerifyStatus] = useState<AuditVerifyStatus | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [savedSearches, setSavedSearches] = useState<AuditSavedSearch[]>([]);
  const [activeTab, setActiveTab] = useState(initialUserIdentifier ? 'events' : 'overview');

  useEffect(() => {
    apiClient
      .listAuditSavedSearches()
      .then((res) => setSavedSearches(res.searches))
      .catch(() => undefined);
  }, []);

  const refreshVerify = useCallback(async () => {
    setVerifying(true);
    try {
      const status = await apiClient.verifyAuditChain();
      setVerifyStatus(status);
    } catch (err) {
      toast.error('Chain verify failed', { description: String(err) });
    } finally {
      setVerifying(false);
    }
  }, []);

  useEffect(() => {
    refreshVerify();
    const interval = window.setInterval(refreshVerify, VERIFY_REFRESH_MS);
    return () => window.clearInterval(interval);
  }, [refreshVerify]);

  const exportFile = useCallback(
    async (format: (typeof EXPORT_FORMATS)[number]) => {
      setIsExporting(true);
      try {
        const blob = await apiClient.exportAuditEvents({ ...filters, limit: EXPORT_LIMIT }, format);
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `audit-events.${format}`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        toast.error('Export failed', { description: String(err) });
      } finally {
        setIsExporting(false);
      }
    },
    [filters]
  );

  const eventTypeValue = useMemo(() => {
    if (Array.isArray(filters.event_type)) return filters.event_type[0] ?? 'all';
    return filters.event_type ?? 'all';
  }, [filters.event_type]);

  const updateFilters = (patch: Partial<AuditFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setRefreshKey((n) => n + 1);
  };

  const updateDbFilters = (patch: Partial<AuditDbFilters>) => {
    setDbFilters((prev) => ({ ...prev, ...patch }));
    setRefreshKey((n) => n + 1);
  };

  const applySavedSearch = (search: AuditSavedSearch) => {
    setFilters({ limit: 100, ...search.filters });
    setRefreshKey((n) => n + 1);
  };

  const saveCurrentSearch = async () => {
    const name = window.prompt('Name this search')?.trim();
    if (!name) return;
    try {
      const created = await apiClient.createAuditSavedSearch(name, filters);
      setSavedSearches((prev) => [...prev, created]);
    } catch (err) {
      toast.error('Failed to save search', { description: String(err) });
    }
  };

  const removeSavedSearch = async (id: string) => {
    try {
      await apiClient.deleteAuditSavedSearch(id);
      setSavedSearches((prev) => prev.filter((search) => search.id !== id));
    } catch (err) {
      toast.error('Failed to delete search', { description: String(err) });
    }
  };

  const handleDrilldown = (patch: Partial<AuditFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setRefreshKey((n) => n + 1);
    setActiveTab('events');
  };

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">Audit Log</h2>
          <p className="text-sm text-muted-foreground">
            Full activity trail — logins, commands, admin actions, data ops.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <VerifyBadge status={verifyStatus} refreshing={verifying} />
          <Button variant="outline" size="sm" onClick={refreshVerify} disabled={verifying}>
            Re-verify
          </Button>
        </div>
      </header>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="events">Events</TabsTrigger>
          <TabsTrigger value="db-events">DB Events</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <AuditOverview onDrilldown={handleDrilldown} />
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <AuditUsersTable onDrilldown={handleDrilldown} />
        </TabsContent>

        <TabsContent value="events" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <UserIdentifierCombobox
              value={filters.user_identifier ?? ''}
              onChange={(v) => updateFilters({ user_identifier: v || undefined })}
              placeholder="User or IP"
            />
            <Select
              value={eventTypeValue}
              onValueChange={(v) => updateFilters({ event_type: v === 'all' ? undefined : [v] })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Event type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All event types</SelectItem>
                {EVENT_TYPE_OPTIONS.flatMap((group) =>
                  group.values.map((v) => (
                    <SelectItem key={v} value={v}>
                      {v}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <Select
              value={filters.source ?? 'all'}
              onValueChange={(v) => updateFilters({ source: v === 'all' ? undefined : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                {SOURCE_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.severity ?? 'all'}
              onValueChange={(v) => updateFilters({ severity: v === 'all' ? undefined : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All severities</SelectItem>
                {SEVERITY_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <DateRangeFilter
                since={filters.since}
                until={filters.until}
                onChange={(since, until) => updateFilters({ since, until })}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" title="Saved searches">
                    <Bookmark className="h-4 w-4 mr-1" /> Saved
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {savedSearches.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">No saved searches</div>
                  ) : (
                    savedSearches.map((search) => (
                      <div key={search.id} className="flex items-center justify-between gap-2 px-2 py-1">
                        <button
                          className="flex-1 truncate text-left text-sm hover:underline"
                          onClick={() => applySavedSearch(search)}
                        >
                          {search.name}
                        </button>
                        <button
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => removeSavedSearch(search.id)}
                          title="Delete saved search"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={saveCurrentSearch}>Save current filters</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {filters.ip ? (
                <Badge
                  variant="secondary"
                  className="cursor-pointer gap-1"
                  onClick={() => updateFilters({ ip: undefined })}
                  title="Clear IP filter"
                >
                  IP: {filters.ip} ×
                </Badge>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={tailing ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTailing((t) => !t)}
              >
                {tailing ? 'Tailing' : 'Tail off'}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={isExporting} title="Export filtered events">
                    <Download className="h-4 w-4 mr-1" /> Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {EXPORT_FORMATS.map((fmt) => (
                    <DropdownMenuItem key={fmt} onClick={() => exportFile(fmt)}>
                      {fmt.toUpperCase()}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <AuditEventTable key={`events-${refreshKey}`} filters={filters} mode="panel" tailing={tailing} />
        </TabsContent>

        <TabsContent value="db-events" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Select
              value={dbFilters.tableName ?? 'all'}
              onValueChange={(v) => updateDbFilters({ tableName: v === 'all' ? undefined : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Table" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tables</SelectItem>
                {DB_TABLE_OPTIONS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={dbFilters.operation ?? 'all'}
              onValueChange={(v) => updateDbFilters({ operation: v === 'all' ? undefined : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Operation" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ops</SelectItem>
                {DB_OP_OPTIONS.map((o) => (
                  <SelectItem key={o} value={o}>
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <AuditDbEventTable key={`db-${refreshKey}`} filters={dbFilters} />
        </TabsContent>
      </Tabs>
    </section>
  );
}
